export interface Event {
  id: number;
  celebration_id: number;
  title: string;
  description: string;
  event_type: string;
  event_date: string;
  start_time: string;  // Event start timestamp
  end_time: string;    // Event end timestamp
  venue_id: number;
  venue_room_id?: number;
  venue_name: string;
  venue_location: string;
  seating_config?: string;
  seating_params?: Record<string, number>;
  seating_generated?: boolean;
  dress_code_enabled?: boolean;
  created_by: number; // Added to match backend and database schema
  status: EventStatus;
  is_public: boolean;
  max_guests: number;
  created_at: Date;
  updated_at: Date;
  deleted_at?: Date;
}

export const EventType = {
  CEREMONY: 'ceremony',
  RECEPTION: 'reception',
  REHEARSAL_DINNER: 'rehearsal_dinner',
  PARTY: 'party',
  RELIGIOUS_RITUAL: 'religious_ritual',
  CULTURAL_EVENT: 'cultural_event',
  MEAL: 'meal',
  ENTERTAINMENT: 'entertainment',
  MEMORIAL_SERVICE: 'memorial_service',
  MEETING: 'meeting',
  FUNDRAISING_EVENT: 'fundraising_event',
  OTHER: 'other'
} as const;

export type EventType = typeof EventType[keyof typeof EventType];

export const EventStatus = {
  DRAFT: 'draft',
  PLANNING: 'planning',
  PLANNED: 'planned',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
} as const;

export type EventStatus = typeof EventStatus[keyof typeof EventStatus];

export interface CreateEventDto {
  celebration_id?: number; // Optional - will be set to Adam & Eva Wedding (ID: 6)
  title: string;
  description?: string;
  event_type: string;
  event_date: string;
  start_time: string;  // Required ISO 8601 timestamp
  end_time: string;    // Required ISO 8601 timestamp
  venue_id?: number; // New field for venue reference
  venue_room_id?: number;
  venue_name?: string; // Legacy field - will be deprecated
  venue_location?: string; // Legacy field - will be deprecated
  seating_config?: string;
  seating_params?: Record<string, number>;
  is_public?: boolean;
  max_guests?: number;
}

export interface UpdateEventDto {
  title?: string;
  description?: string;
  event_type?: string;
  event_date?: string;
  start_time?: string;  // Optional ISO 8601 timestamp
  end_time?: string;    // Optional ISO 8601 timestamp
  venue_id?: number;
  venue_room_id?: number;
  venue_name?: string;
  venue_location?: string;
  seating_config?: string;
  seating_params?: Record<string, number>;
  status?: EventStatus;
  is_public?: boolean;
  max_guests?: number;
}

export interface EventMember {
  id: number;
  event_id: number;
  user_id: number;
  role: EventMemberRole;
  permissions: string[];
  added_at: string;
  user?: {
    id: number;
    username: string;
    full_name: string;
    email: string;
  };
}

export const EventMemberRole = {
  ORGANIZER: 'organizer',
  EVENT_MANAGER: 'event_manager',
  MEMBER: 'member',
  CELEBRANT: 'celebrant'
} as const;

export type EventMemberRole = typeof EventMemberRole[keyof typeof EventMemberRole];

export interface CreateEventMemberDto {
  user_id: number;
  role: EventMemberRole;
  permissions?: string[];
}

export interface ValidationError {
  field: string;
  message: string;
  constraint: string;
  validRange?: {
    min: Date;
    max: Date;
  };
}
